import { type FileId, type UploadedFile } from "@inrev/common";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useRequest } from "../../utils/request";
import { boundedPoll } from "../bounded-poll";
import {
	CLASSIFICATION_POLL_INTERVAL_MS,
	CLASSIFICATION_POLL_MAX_MS,
	isClassificationPendingStatus,
} from "./classification-poll";
import { filesQueryKey } from "./files.keys";

export type UseFetchFileMetaOptions = {
	/** SSE fallback: poll while classification is pending. Defaults to false. */
	pollWhileClassificationPending?: boolean;
};

const shouldPollClassification = (file: UploadedFile | undefined) =>
	isClassificationPendingStatus(file?.classificationStatus);

export const useFetchFileMeta = (fileId: FileId, options: UseFetchFileMetaOptions = {}) => {
	const { pollWhileClassificationPending = false } = options;
	const { get } = useRequest();
	const pollStartedAtMsRef = useRef<number | null>(null);

	useEffect(() => {
		pollStartedAtMsRef.current = null;
	}, [fileId]);

	return useQuery({
		queryKey: filesQueryKey(fileId),
		queryFn: () => get<UploadedFile>(`/v2/files/${fileId}/meta`),
		staleTime: Number.POSITIVE_INFINITY,
		refetchInterval: (query) => {
			if (!pollWhileClassificationPending) return false;

			return boundedPoll({
				data: query.state.data,
				shouldPoll: shouldPollClassification,
				startedAtMsRef: pollStartedAtMsRef,
				intervalMs: CLASSIFICATION_POLL_INTERVAL_MS,
				maxMs: CLASSIFICATION_POLL_MAX_MS,
			});
		},
	});
};
